import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./ChangeContent.module.scss";
import { isNullish } from "utils/utils";
import Divider from "components/Divider/Divider";
import TextMAS from "components/TextMAS/TextMAS";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const COMPONENT_LOCALE_KEY = "page.finance.fxRate.changeFunction.";

const ChangeContent = ({ handleInput, data, error, setError }) => {
  const { t } = useTranslation();
  const buyRef = useRef(5);
  const sellRef = useRef(5);
  const [inputData, setInputData] = useState({
    cost: data.cost,
    fxRate: data.fxRate,
    bankSellCost: data.bankSellCost,
    bankSellFxRate: data.bankSellFxRate,
  });
  const [focus, setFocus] = useState({});

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    handleExchangeRateInput(value, field);
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const fxRatePercentageOnChange = (e) => {
    const { value } = e.target;
    buyRef.current = +value;
  };

  const bankSellFxRatePercentageOnChange = (e) => {
    const { value } = e.target;
    sellRef.current = +value;
  };

  const handleExchangeRateInput = (value, field) => {
    if (!isNaN(Number(value)) && +Number(value) !== +Number(value).toFixed(6)) {
      return;
    } else {
      setInputData({ ...inputData, [field]: value });
      handleInput(!value ? undefined : +Number(value), field);
    }
  };

  const calculateBuyFxRate = () => {
    const { cost } = data;

    let hasError = false;
    if (isNullish(cost)) {
      setError((prev) => ({ ...prev, costMissing: true }));
      hasError = true;
    } else if (isNaN(cost) || Number(cost) <= 0) {
      setError((prev) => ({ ...prev, costInvalid: true }));
      hasError = true;
    } else {
      setError((prev) => ({ ...prev, costMissing: false, costInvalid: false }));
    }
    if (!buyRef.current || isNaN(+buyRef.current)) {
      setError((prev) => ({ ...prev, buyInvalid: true }));
      hasError = true;
    }
    if (hasError) return;
    setError((prev) => ({
      ...prev,
      costMissing: false,
      costInvalid: false,
      fxRateMissing: false,
      fxRateInvalidm: false,
      buyInvalid: false,
    }));

    const newFxRate = +cost * (1 - +buyRef.current / 100);
    setInputData({
      ...inputData,
      fxRate: +Number(newFxRate).toFixed(6).toString(),
    });
    handleInput(+Number(newFxRate).toFixed(6), "fxRate");
  };

  const calculateSellFxRate = () => {
    const { bankSellCost } = data;

    let hasError = false;
    if (isNullish(bankSellCost)) {
      setError((prev) => ({ ...prev, bankSellCostMissing: true }));
      hasError = true;
    } else if (isNaN(bankSellCost) || Number(bankSellCost) <= 0) {
      setError((prev) => ({ ...prev, bankSellCostInvalid: true }));
      hasError = true;
    } else {
      setError((prev) => ({
        ...prev,
        bankSellCostMissing: false,
        bankSellCostInvalid: false,
      }));
    }
    if (!sellRef.current || isNaN(+sellRef.current)) {
      setError((prev) => ({ ...prev, sellInvalid: true }));
      hasError = true;
    }
    if (hasError) return;
    setError((prev) => ({
      ...prev,
      bankSellCostMissing: false,
      bankSellCostInvalid: false,
      bankSellCostMissing: false,
      bankSellCostInvalid: false,
      sellInvalid: false,
    }));

    const newSellFxRate = +bankSellCost * (1 + +sellRef.current / 100);
    setInputData({
      ...inputData,
      bankSellFxRate: +Number(newSellFxRate).toFixed(6).toString(),
    });
    handleInput(+Number(newSellFxRate).toFixed(6), "bankSellFxRate");
  };

  return (
    <div className={s["content-container"]}>
      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.1")}
        disabled
        defaultValue={
          data.exchangeDate
            ? moment(data.exchangeDate).format("YYYY-MM-DD")
            : ""
        }
        sxParams={{ textFocus: undefined }}
      />

      <Selection
        disabled
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.2")}
        selectionValue={data.currencyCode ? data.currencyCode : ""}
        options={transactionCurrencyOptions("all")}
        sxParams={{ selectionFocus: undefined }}
      />

      <Divider margin="16px" />

      <TextMAS
        fontSize="large"
        fontWeight="medium"
        color="brandDark64"
        style={{ paddingBottom: "16px" }}
      >
        {t(COMPONENT_LOCALE_KEY + "sectionTitle.1")}
      </TextMAS>

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.3")}
        mandatory
        value={inputData.cost}
        onchangeFn={(e) => textInputOnchange(e, "cost")}
        onfocusFn={() => textInputOnFocus("cost")}
        onblurFn={() => textInputOnBlur("cost")}
        errorMessage={
          error.costMissing
            ? t(COMPONENT_LOCALE_KEY + "errorMessageMissing.cost")
            : t(COMPONENT_LOCALE_KEY + "errorMessageInvalid.cost")
        }
        sxParams={{
          textFocus: focus.cost,
          textError: error.costMissing || error.costInvalid,
        }}
      />

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.4")}
        mandatory
        value={inputData.fxRate}
        onchangeFn={(e) => textInputOnchange(e, "fxRate")}
        onfocusFn={() => textInputOnFocus("fxRate")}
        onblurFn={() => textInputOnBlur("fxRate")}
        errorMessage={
          error.fxRateMissing
            ? t(COMPONENT_LOCALE_KEY + "errorMessageMissing.fxRate")
            : t(COMPONENT_LOCALE_KEY + "errorMessageInvalid.fxRate")
        }
        sxParams={{
          textFocus: focus.fxRate,
          textError: error.fxRateMissing || error.fxRateInvalid,
        }}
      />

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.5")}
        defaultValue="5"
        onchangeFn={fxRatePercentageOnChange}
        onfocusFn={() => textInputOnFocus("fxRatePercentage")}
        onblurFn={() => textInputOnBlur("fxRatePercentage")}
        errorMessage={t(
          COMPONENT_LOCALE_KEY + "errorMessageInvalid.fxRatePercentage",
        )}
        sxParams={{
          textFocus: focus.fxRatePercentage,
          textError: error.buyInvalid,
        }}
      />

      <div>
        <ButtonMAS onClick={calculateBuyFxRate}>
          {t(COMPONENT_LOCALE_KEY + "calculateButton.1")}
        </ButtonMAS>
      </div>

      <Divider margin="16px" />

      <TextMAS
        fontSize="large"
        fontWeight="medium"
        color="brandDark64"
        style={{ paddingBottom: "16px" }}
      >
        {t(COMPONENT_LOCALE_KEY + "sectionTitle.2")}
      </TextMAS>

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.6")}
        mandatory
        value={inputData.bankSellCost}
        onchangeFn={(e) => textInputOnchange(e, "bankSellCost")}
        onfocusFn={() => textInputOnFocus("bankSellCost")}
        onblurFn={() => textInputOnBlur("bankSellCost")}
        errorMessage={
          error.fxRateMissing
            ? t(COMPONENT_LOCALE_KEY + "errorMessageMissing.bankSellCost")
            : t(COMPONENT_LOCALE_KEY + "errorMessageInvalid.bankSellCost")
        }
        sxParams={{
          textFocus: focus.bankSellCost,
          textError: error.bankSellCostMissing || error.bankSellCostInvalid,
        }}
      />

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.7")}
        mandatory
        value={inputData.bankSellFxRate}
        onchangeFn={(e) => textInputOnchange(e, "bankSellFxRate")}
        onfocusFn={() => textInputOnFocus("bankSellFxRate")}
        onblurFn={() => textInputOnBlur("bankSellFxRate")}
        errorMessage={
          error.fxRateMissing
            ? t(COMPONENT_LOCALE_KEY + "errorMessageMissing.bankSellFxRate")
            : t(COMPONENT_LOCALE_KEY + "errorMessageInvalid.bankSellFxRate")
        }
        sxParams={{
          textFocus: focus.bankSellFxRate,
          textError: error.bankSellFxRateMissing || error.bankSellFxRateInvalid,
        }}
      />

      <OutlinedText
        label={t(COMPONENT_LOCALE_KEY + "dialogContent.8")}
        defaultValue="5"
        onchangeFn={bankSellFxRatePercentageOnChange}
        onfocusFn={() => textInputOnFocus("bankSellFxRatePercentage")}
        onblurFn={() => textInputOnBlur("bankSellFxRatePercentage")}
        errorMessage={t(
          COMPONENT_LOCALE_KEY + "errorMessageInvalid.bankSellFxRatePercentage",
        )}
        sxParams={{
          textFocus: focus.bankSellFxRatePercentage,
          textError: error.sellInvalid,
        }}
      />

      <div>
        <ButtonMAS onClick={calculateSellFxRate}>
          {t(COMPONENT_LOCALE_KEY + "calculateButton.1")}
        </ButtonMAS>
      </div>
    </div>
  );
};

export default React.memo(ChangeContent);
